/* <product-gallery> — PDP media gallery.
   Interaction model (Figma): thumbnails select the main media; the arrows
   and dots slide the THUMBNAIL RAIL only. Dots are one per rail SCROLL
   POSITION (media − visible + 1), not one per media item — every dot is
   reachable and the arrows advance exactly one position per click. The
   4-per-view rail paging doesn't fit <carousel-component>'s
   one-slide-per-page transform model, so the rail logic lives here (same
   reasoning as reviews-slider.js).
   Also subscribes to PUB_SUB_EVENTS.variantChange to show the variant's
   featured media and refresh the discount badge (Dawn's product-info.js
   setActiveMedia call is optional-chained against `media-gallery`, so it
   no-ops here). */

if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        this.rail = this.querySelector('[data-gallery-rail]');
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
        this.prevBtn = this.querySelector('[data-gallery-prev]');
        this.nextBtn = this.querySelector('[data-gallery-next]');
        this.dotsRoot = this.querySelector('[data-gallery-dots]');
        this.controls = this.querySelector('[data-gallery-controls]');
        this.magnify = this.querySelector('[data-gallery-magnify]');
        this._reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._dotCount = 0;

        this.thumbs.forEach((btn) => {
          btn.addEventListener('click', () => this.showMedia(parseInt(btn.dataset.thumbIndex, 10)));
        });
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._scrollRailBy(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._scrollRailBy(1));
        this._bindImageZoom();

        if (this.rail) {
          this.rail.addEventListener('scroll', () => {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
              this._raf = null;
              this._syncRail();
            });
          });
          // Position count depends on layout (thumb width is a % of the
          // rail) — rebuild dots whenever the rail resizes; the initial
          // observation also fires once layout exists.
          this._resizeObserver = new ResizeObserver(() => this._refreshDots());
          this._resizeObserver.observe(this.rail);
          this._refreshDots();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, html, variant } }) => {
            const productInfo = this.closest('product-info');
            if (!productInfo) return;
            const ownSectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
            if (sectionId !== ownSectionId) return;

            const mediaId = variant?.featured_media?.id;
            if (mediaId) {
              const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
              if (index >= 0) {
                this.showMedia(index);
                this._scrollRailToThumb(index);
              }
            }

            const badge = this.querySelector('[data-discount-badge]');
            const newBadge = html.querySelector('[data-discount-badge]');
            if (badge && newBadge) {
              badge.textContent = newBadge.textContent;
              badge.hidden = newBadge.hidden;
            }
          });
        }

        // Deep-linked variants (?variant=) start on their featured media,
        // matching what the price and pills already show.
        let initial = 0;
        if (this.dataset.initialMediaId) {
          const i = this.slides.findIndex((slide) => slide.dataset.mediaId === this.dataset.initialMediaId);
          if (i > 0) initial = i;
        }
        this.showMedia(initial, true);
        if (initial > 0 && this.rail && this.thumbs[initial]) {
          this.rail.scrollTo({ left: this.thumbs[initial].offsetLeft - this.rail.offsetLeft, behavior: 'auto' });
        }
      }

      disconnectedCallback() {
        if (this._unsubscribe) this._unsubscribe();
        if (this._resizeObserver) this._resizeObserver.disconnect();
      }

      showMedia(index, instant = false) {
        if (!this.track || !this.slides.length) return;
        this._resetMedia();
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        // The magnify opener always opens the media currently in view.
        if (this.magnify) this.magnify.dataset.mediaId = this.slides[this.index].dataset.mediaId;

        if (this._reduceMotion || instant) {
          const prevTransition = this.track.style.transition;
          this.track.style.transition = 'none';
          this.track.style.transform = `translateX(-${this.index * 100}%)`;
          void this.track.offsetWidth;
          this.track.style.transition = prevTransition;
        } else {
          this.track.style.transform = `translateX(-${this.index * 100}%)`;
        }

        this.slides.forEach((slide, i) => {
          const inactive = i !== this.index;
          slide.setAttribute('aria-hidden', inactive ? 'true' : 'false');
          slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
            if (inactive) el.setAttribute('tabindex', '-1');
            else el.removeAttribute('tabindex');
          });
        });
      }

      /* Delegated from the component root rather than bound to the slide
         images: the gallery markup is re-rendered after this element is
         upgraded, and a re-render swaps the child nodes, which silently
         drops any listener held on them. The root survives that.
         Capture phase, because magnify.js binds image.onclick for the same
         purpose: capture reaches us before the target's own handler, so
         stopImmediatePropagation there keeps one click from zooming twice. */
      _bindImageZoom() {
        this.addEventListener(
          'click',
          (event) => {
            if (event.target.tagName !== 'IMG') return;
            const slide = event.target.closest('[data-gallery-slide]');
            if (!slide) return;
            // A video slide renders a <deferred-media> poster whose
            // <img> sits inside the play button. Zooming it here would
            // swallow the click that loads the media.
            if (slide.querySelector('deferred-media')) return;
            event.stopImmediatePropagation();
            this._toggleZoom(slide, event);
          },
          true
        );
      }

      /* Scales the existing image rather than laying a new element over it.
         An overlay div was reliably created but never painted: this store
         runs app stylesheets (Judge.me, Rebuy, Mailmunch) that are
         cross-origin and unreadable, and one of them resolved the overlay to
         display:none. Transforming a node the app CSS has already accepted
         leaves nothing new for it to match. The slider viewport clips the
         overflow, which is what keeps the zoom inside the frame. */
      _toggleZoom(slide = null, event = null) {
        // Queried live rather than off this.slides: the button path has to
        // reach the slide that is in the page now, not the one cached when
        // this element was upgraded.
        const target = slide || this.querySelectorAll('[data-gallery-slide]')[this.index || 0];
        if (!target) return;

        const image = target.querySelector('img');
        if (!image) return;

        const reopening = this._zoomImage === image;
        this._closeZoom();
        if (reopening) return;

        this._zoomImage = image;
        this._zoomMove = (move) => this._panZoom(move);
        this._zoomLeave = () => this._closeZoom();
        image.addEventListener('mousemove', this._zoomMove);
        image.addEventListener('mouseleave', this._zoomLeave);

        image.style.willChange = 'transform';
        image.style.transform = 'scale(2)';
        this._panZoom(event);
      }

      _closeZoom() {
        const image = this._zoomImage;
        if (!image) return;

        image.removeEventListener('mousemove', this._zoomMove);
        image.removeEventListener('mouseleave', this._zoomLeave);
        image.style.transform = '';
        image.style.transformOrigin = '';
        image.style.willChange = '';
        this._zoomImage = null;
        this._zoomMove = null;
        this._zoomLeave = null;
      }

      /* transform-origin under the pointer holds that point still while the
         image scales, so the frame shows the part of the photo being pointed
         at. Measured off the slide, not the image: the image's own rect is
         already scaled once the transform applies. */
      _panZoom(event) {
        const image = this._zoomImage;
        if (!image) return;

        const frame = image.parentElement.getBoundingClientRect();
        if (!frame.width || !frame.height) return;

        const clamp = (value) => Math.min(Math.max(value, 0), 1);
        const x = event ? clamp((event.clientX - frame.left) / frame.width) : 0.5;
        const y = event ? clamp((event.clientY - frame.top) / frame.height) : 0.5;
        image.style.transformOrigin = `${x * 100}% ${y * 100}%`;
      }

      /* Changing slide puts every loaded video back to its poster: the built
         <video>/<iframe> is dropped and the loaded flag cleared, so returning
         to the slide shows the play button again and the next click rebuilds
         it from the <template>. Removing the element is also what stops
         playback, hosted or embedded. Scoped to this gallery so a thumbnail
         click doesn't touch media elsewhere on the page. */
      _resetMedia() {
        this.querySelectorAll('deferred-media[loaded]').forEach((media) => {
          media.querySelectorAll('video, iframe').forEach((el) => el.remove());
          media.removeAttribute('loaded');
        });
      }

      _behavior() {
        return this._reduceMotion ? 'auto' : 'smooth';
      }

      /* Distance between two thumb starts = one scroll position. */
      _step() {
        return this.thumbs.length > 1 ? this.thumbs[1].offsetLeft - this.thumbs[0].offsetLeft : 0;
      }

      _maxScroll() {
        return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      }

      _positionCount() {
        const step = this._step();
        const max = this._maxScroll();
        if (!step || max <= 0) return 1;
        return Math.round(max / step) + 1;
      }

      _refreshDots() {
        if (!this.rail) return;
        // Nothing to scroll (4 or fewer thumbs in view) → the whole
        // controls row is pointless; .hidden is Dawn's display:none utility.
        if (this.controls) this.controls.classList.toggle('hidden', this._maxScroll() <= 0);
        if (this.dotsRoot) {
          const count = this._positionCount();
          if (count !== this._dotCount) {
            this._dotCount = count;
            this.dotsRoot.innerHTML = '';
            for (let i = 0; i < count; i++) {
              const li = document.createElement('li');
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'slider__dot';
              const labelTemplate = this.dotsRoot.dataset.dotLabel || 'Go to slide %d';
              btn.setAttribute('aria-label', labelTemplate.replace('%d', i + 1));
              btn.addEventListener('click', () => {
                this.rail.scrollTo({ left: i * this._step(), behavior: this._behavior() });
              });
              li.appendChild(btn);
              this.dotsRoot.appendChild(li);
            }
            this.dots = Array.from(this.dotsRoot.querySelectorAll('button'));
          }
        }
        this._syncRail();
      }

      _scrollRailToThumb(i) {
        const thumb = this.thumbs[i];
        if (!this.rail || !thumb) return;
        this.rail.scrollTo({ left: thumb.offsetLeft - this.rail.offsetLeft, behavior: this._behavior() });
      }

      _scrollRailBy(direction) {
        if (!this.rail) return;
        const step = this._step() || this.rail.clientWidth;
        this.rail.scrollBy({ left: direction * step, behavior: this._behavior() });
      }

      _syncRail() {
        if (!this.rail) return;
        const max = this._maxScroll();
        const x = this.rail.scrollLeft;

        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', x <= 1);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', x >= max - 1);

        if (this.dots && this.dots.length) {
          const step = this._step();
          const active = step ? Math.min(Math.round(x / step), this.dots.length - 1) : 0;
          this.dots.forEach((dot, i) => {
            if (i === active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }
      }
    }
  );
}
